import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  buildCommerceAuditColumns,
  formatCommerceDateOnly,
  useCommerceUserTimezone,
} from '../utils/commerceListDateUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMERCE_DEFAULT_PAGE_SIZE, commerceServerTablePagination } from '../constants/commerceListPagination';
import { resolveLocalizedEntityName } from '../constants/commerceNameLocales';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';

dayjs.extend(utc);

const { Title, Text } = Typography;

const apiBase = '/api/commerce/admin/wpa-home-promotions';

/** 與後端 BuildUploadsPublicPath 一致，供縮圖預覽 */
function toUploadsPublicUrl(relativePath) {
  if (!relativePath || typeof relativePath !== 'string') return null;
  const trimmed = relativePath.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const n = trimmed.replace(/\\/g, '/').replace(/^\//, '');
  if (n.toLowerCase().startsWith('public/')) return `/${n}`;
  return `/Uploads/${n}`;
}

const PICKER_STATUS_OPTIONS = [
  { value: 'active', label: 'active' },
  { value: 'inactive', label: 'inactive' },
];
const PICKER_AVAILABILITY_OPTIONS = [
  { value: 'in stock', label: 'in stock' },
  { value: 'out of stock', label: 'out of stock' },
];

export default function CommerceWpaHomePromotionPage() {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [form] = Form.useForm();
  const bannerImagePath = Form.useWatch('bannerImagePath', form);
  const fullscreenImagePath = Form.useWatch('fullscreenImagePath', form);
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linkedProductSummary, setLinkedProductSummary] = useState(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerLoading, setProductPickerLoading] = useState(false);
  const [productPickerRows, setProductPickerRows] = useState([]);
  const [productPickerTotal, setProductPickerTotal] = useState(0);
  const [productPickerPage, setProductPickerPage] = useState(1);
  const [productPickerPageSize, setProductPickerPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [productPickerSearch, setProductPickerSearch] = useState('');
  const [productPickerStatus, setProductPickerStatus] = useState(undefined);
  const [productPickerAvailability, setProductPickerAvailability] = useState(undefined);
  const [productPickerBrand, setProductPickerBrand] = useState('');
  /** 已套用至 API 的品牌篩選（避免輸入時每字元都 refetch） */
  const productPickerBrandAppliedRef = useRef('');
  const pickerSelectedRowRef = useRef(null);
  const [pickerSelectedKey, setPickerSelectedKey] = useState(null);
  const productId = Form.useWatch('productId', form);

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const companyQuery = useMemo(() => {
    if (!isTenantAdmin || !companyFilter) return '';
    return `?companyId=${companyFilter}`;
  }, [isTenantAdmin, companyFilter]);

  const loadCompanies = useCallback(async () => {
    if (!isTenantAdmin) return;
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  }, [fetchWithAuth, isTenantAdmin]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const q = isTenantAdmin && companyFilter ? `?companyId=${companyFilter}` : '';
      const res = await fetchWithAuth(`${apiBase}${q}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err?.error || t('commerceWpaHomePromotions.loadFailed'));
        setRows([]);
        return;
      }
      const json = await res.json();
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      message.error(`${t('commerceWpaHomePromotions.loadFailed')}: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, isTenantAdmin, companyFilter, t]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (isTenantAdmin && !companyFilter && companiesForSelect.length === 1) {
      setCompanyFilter(companiesForSelect[0].id);
    }
  }, [isTenantAdmin, companyFilter, companiesForSelect]);

  useEffect(() => {
    if (!isTenantAdmin || companyFilter) {
      loadList();
    }
  }, [loadList, isTenantAdmin, companyFilter]);

  const loadProductPickerList = useCallback(async () => {
    const cid = isTenantAdmin ? companyFilter : userInfo.company_id;
    if (!cid) return;
    setProductPickerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(productPickerPage));
      params.set('pageSize', String(productPickerPageSize));
      if (productPickerSearch.trim()) params.set('search', productPickerSearch.trim());
      if (isTenantAdmin && companyFilter) params.set('companyId', companyFilter);
      if (productPickerStatus) params.set('status', productPickerStatus);
      if (productPickerAvailability) params.set('availability', productPickerAvailability);
      if (productPickerBrandAppliedRef.current.trim()) {
        params.set('brand', productPickerBrandAppliedRef.current.trim());
      }
      const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const total = typeof data.total === 'number' ? data.total : items.length;
      setProductPickerRows(items);
      setProductPickerTotal(total);
    } catch {
      setProductPickerRows([]);
      setProductPickerTotal(0);
    } finally {
      setProductPickerLoading(false);
    }
  }, [
    fetchWithAuth,
    isTenantAdmin,
    companyFilter,
    userInfo.company_id,
    productPickerPage,
    productPickerPageSize,
    productPickerSearch,
    productPickerStatus,
    productPickerAvailability,
  ]);

  useEffect(() => {
    if (!productPickerOpen) return;
    loadProductPickerList();
  }, [productPickerOpen, loadProductPickerList]);

  const productPickerColumns = useMemo(
    () => [
      {
        title: t('commerceProducts.image'),
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        width: 72,
        fixed: 'left',
        render: (url) =>
          url ? (
            <img src={url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }} />
          ) : (
            '—'
          ),
      },
      {
        title: t('commerceProducts.nameColUiLocale'),
        dataIndex: 'name',
        key: 'name',
        width: 200,
        ellipsis: true,
        render: (value, record) =>
          resolveLocalizedEntityName(value, record.nameLocales ?? record.name_locales, currentLanguage) || '—',
      },
      {
        title: t('commerceProducts.contentId'),
        dataIndex: 'productRetailerId',
        key: 'productRetailerId',
        width: 140,
        ellipsis: true,
      },
      ...(isTenantAdmin
        ? [
            {
              title: t('commerceProducts.company'),
              dataIndex: 'companyId',
              key: 'companyId',
              width: 160,
              ellipsis: true,
              render: (id) => companiesForSelect.find((c) => c.id === id)?.name || id || '—',
            },
          ]
        : []),
      {
        title: t('commerceProducts.brand'),
        dataIndex: 'brand',
        key: 'brand',
        width: 120,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: t('commerceProducts.description'),
        dataIndex: 'description',
        key: 'description',
        width: 180,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: t('commerceProducts.price'),
        key: 'price',
        width: 120,
        render: (_, record) =>
          record.price == null ? '—' : `${record.currency || 'HKD'} ${Number(record.price).toFixed(2)}`,
      },
      {
        title: t('commerceProducts.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v) => (
          <Tag color={String(v).toLowerCase() === 'active' ? 'green' : 'default'}>{v || '—'}</Tag>
        ),
      },
      {
        title: t('commerceProducts.unitOfMeasure'),
        dataIndex: 'unitOfMeasure',
        key: 'unitOfMeasure',
        width: 90,
        render: (v) => v || '—',
      },
      {
        title: t('commerceProducts.availability'),
        dataIndex: 'availability',
        key: 'availability',
        width: 110,
        render: (v) => v || '—',
      },
    ],
    [t, currentLanguage, isTenantAdmin, companiesForSelect]
  );

  const openProductPicker = () => {
    const cid = isTenantAdmin ? companyFilter : userInfo.company_id;
    if (!cid) {
      message.warning(t('commerceWpaHomePromotions.selectCompany'));
      return;
    }
    setProductPickerPage(1);
    setProductPickerSearch('');
    setProductPickerStatus(undefined);
    setProductPickerAvailability(undefined);
    setProductPickerBrand('');
    productPickerBrandAppliedRef.current = '';
    setPickerSelectedKey(form.getFieldValue('productId') || null);
    pickerSelectedRowRef.current = null;
    setProductPickerOpen(true);
  };

  const confirmPickProduct = async () => {
    if (!pickerSelectedKey) {
      message.warning(t('commerceWpaHomePromotions.pickerNeedSelect'));
      return;
    }
    let row = pickerSelectedRowRef.current || productPickerRows.find((r) => r.id === pickerSelectedKey);
    if (!row) {
      try {
        const res = await fetchWithAuth(`/api/commerce/products/${pickerSelectedKey}`);
        if (res.ok) row = await res.json();
      } catch {
        /* ignore */
      }
    }
    if (!row) {
      message.warning(t('commerceWpaHomePromotions.pickerNeedSelect'));
      return;
    }
    const displayName =
      resolveLocalizedEntityName(row.name, row.nameLocales ?? row.name_locales, currentLanguage) || row.name || '';
    form.setFieldsValue({ productId: row.id });
    setLinkedProductSummary({
      id: row.id,
      productRetailerId: row.productRetailerId,
      name: row.name,
      displayName,
    });
    setProductPickerOpen(false);
  };

  const uploadImage = async (file, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const q = isTenantAdmin && companyFilter ? `?companyId=${companyFilter}` : '';
    const res = await fetchWithAuth(`${apiBase}/upload${q}`, { method: 'POST', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'upload failed');
    }
    const data = await res.json();
    return data.path;
  };

  const openCreate = () => {
    setEditing(null);
    setLinkedProductSummary(null);
    form.resetFields();
    form.setFieldsValue({
      sortOrder: 0,
      isActive: true,
      bannerImagePath: '',
      fullscreenImagePath: '',
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      sortOrder: record.sortOrder,
      isActive: record.isActive,
      validFrom: record.validFrom ? dayjs.utc(record.validFrom) : null,
      validTo: record.validTo ? dayjs.utc(record.validTo) : null,
      bannerImagePath: record.bannerImagePath || '',
      fullscreenImagePath: record.fullscreenImagePath || '',
      productId: record.productId || undefined,
    });
    if (record.productId) {
      setLinkedProductSummary({
        id: record.productId,
        productRetailerId: record.productItemNo,
        name: record.productName,
        displayName: record.productName || '',
      });
    } else {
      setLinkedProductSummary(null);
    }
    setModalOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        sortOrder: v.sortOrder ?? 0,
        isActive: !!v.isActive,
        validFrom: v.validFrom ? v.validFrom.utc().toISOString() : null,
        validTo: v.validTo ? v.validTo.utc().endOf('day').toISOString() : null,
        bannerImagePath: v.bannerImagePath || null,
        fullscreenImagePath: v.fullscreenImagePath || null,
        productId: v.productId || null,
      };
      const q = isTenantAdmin && companyFilter ? `?companyId=${companyFilter}` : '';
      const url = editing ? `${apiBase}/${editing.id}${q}` : `${apiBase}${q}`;
      const res = await fetchWithAuth(url, {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err?.error || t('commerceWpaHomePromotions.saveFailed'));
        return;
      }
      message.success(t('commerceWpaHomePromotions.saveOk'));
      setModalOpen(false);
      loadList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message);
    }
  };

  const remove = (record) => {
    Modal.confirm({
      title: t('commerceWpaHomePromotions.deleteConfirm'),
      onOk: async () => {
        const q = isTenantAdmin && companyFilter ? `?companyId=${companyFilter}` : '';
        const res = await fetchWithAuth(`${apiBase}/${record.id}${q}`, { method: 'DELETE' });
        if (!res.ok) {
          message.error(t('commerceWpaHomePromotions.deleteFailed'));
          return;
        }
        message.success(t('commerceWpaHomePromotions.deleteOk'));
        loadList();
      },
    });
  };

  const columns = [
    { title: t('commerceWpaHomePromotions.colSort'), dataIndex: 'sortOrder', width: 72 },
    {
      title: t('commerceWpaHomePromotions.colBanner'),
      dataIndex: 'bannerUrl',
      width: 100,
      render: (url) => (url ? <Image src={url} width={72} height={45} style={{ objectFit: 'cover' }} /> : '—'),
    },
    {
      title: t('commerceWpaHomePromotions.colFullscreen'),
      dataIndex: 'fullscreenUrl',
      width: 100,
      render: (url) => (url ? <Image src={url} width={48} height={86} style={{ objectFit: 'cover' }} /> : '—'),
    },
    {
      title: t('commerceWpaHomePromotions.colProduct'),
      key: 'product',
      render: (_, r) => (r.productItemNo ? `${r.productItemNo} ${r.productName || ''}` : '—'),
    },
    {
      title: t('commerceWpaHomePromotions.colActive'),
      dataIndex: 'isActive',
      width: 80,
      render: (x) => (x ? t('commerceWpaHomePromotions.yes') : t('commerceWpaHomePromotions.no')),
    },
    {
      title: t('commerceWpaHomePromotions.colSchedule'),
      key: 'sched',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.validFrom ? formatCommerceDateOnly(r.validFrom, userTimezoneOffset) : '—'} →{' '}
          {r.validTo ? formatCommerceDateOnly(r.validTo, userTimezoneOffset) : '—'}
        </span>
      ),
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commerceWpaHomePromotions.colActions'),
      key: 'act',
      width: 120,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="center">
            <Title level={4} style={{ margin: 0 }}>
              {t('commerceWpaHomePromotions.title')}
            </Title>
            {isTenantAdmin && (
              <Select
                allowClear
                placeholder={t('commerceWpaHomePromotions.selectCompany')}
                style={{ minWidth: 220 }}
                options={companiesForSelect.map((c) => ({ value: c.id, label: c.name }))}
                value={companyFilter}
                onChange={(v) => setCompanyFilter(v)}
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={loadList}>
              {t('commerceWpaHomePromotions.refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={isTenantAdmin && !companyFilter}>
              {t('commerceWpaHomePromotions.add')}
            </Button>
          </Space>

          <Alert type="info" showIcon message={t('commerceWpaHomePromotions.sizeHintTitle')} description={t('commerceWpaHomePromotions.sizeHintBody')} />

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 900 }}
          />
        </Space>
      </Card>

      <Modal
        title={editing ? t('commerceWpaHomePromotions.edit') : t('commerceWpaHomePromotions.create')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="sortOrder" label={t('commerceWpaHomePromotions.sortOrder')} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label={t('commerceWpaHomePromotions.active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="validFrom" label={t('commerceWpaHomePromotions.validFrom')}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="validTo" label={t('commerceWpaHomePromotions.validTo')}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('commerceWpaHomePromotions.bannerPath')} extra={t('commerceWpaHomePromotions.uploadBanner')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                maxCount={1}
                showUploadList={false}
                beforeUpload={async (file) => {
                  try {
                    const path = await uploadImage(file, 'banner');
                    form.setFieldsValue({ bannerImagePath: path });
                    message.success(t('commerceWpaHomePromotions.uploadOk'));
                  } catch (e) {
                    message.error(e.message);
                  }
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>{t('commerceWpaHomePromotions.uploadBannerBtn')}</Button>
              </Upload>
              {bannerImagePath ? (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Image
                    src={toUploadsPublicUrl(bannerImagePath)}
                    alt=""
                    style={{
                      width: 200,
                      aspectRatio: '16 / 10',
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #f0f0f0',
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all', flex: 1, minWidth: 120 }}>
                    {bannerImagePath}
                  </Text>
                </div>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="bannerImagePath" hidden>
            <Input />
          </Form.Item>
          <Form.Item label={t('commerceWpaHomePromotions.fullscreenPath')} extra={t('commerceWpaHomePromotions.uploadFullscreen')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                maxCount={1}
                showUploadList={false}
                beforeUpload={async (file) => {
                  try {
                    const path = await uploadImage(file, 'fullscreen');
                    form.setFieldsValue({ fullscreenImagePath: path });
                    message.success(t('commerceWpaHomePromotions.uploadOk'));
                  } catch (e) {
                    message.error(e.message);
                  }
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>{t('commerceWpaHomePromotions.uploadFullscreenBtn')}</Button>
              </Upload>
              {fullscreenImagePath ? (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Image
                    src={toUploadsPublicUrl(fullscreenImagePath)}
                    alt=""
                    style={{
                      width: 120,
                      aspectRatio: '9 / 16',
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #f0f0f0',
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all', flex: 1, minWidth: 120 }}>
                    {fullscreenImagePath}
                  </Text>
                </div>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="fullscreenImagePath" hidden>
            <Input />
          </Form.Item>
          <Form.Item label={t('commerceWpaHomePromotions.linkedProduct')}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Space wrap align="center">
                <Text style={{ flex: 1, minWidth: 200 }}>
                  {linkedProductSummary
                    ? `${linkedProductSummary.productRetailerId || ''} — ${linkedProductSummary.displayName || linkedProductSummary.name || ''}`.trim()
                    : t('commerceWpaHomePromotions.noLinkedProduct')}
                </Text>
                <Button
                  type="primary"
                  ghost
                  icon={<SearchOutlined />}
                  onClick={openProductPicker}
                  disabled={isTenantAdmin && !companyFilter}
                >
                  {t('commerceWpaHomePromotions.pickProductBtn')}
                </Button>
                {productId ? (
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => {
                      form.setFieldsValue({ productId: undefined });
                      setLinkedProductSummary(null);
                    }}
                  >
                    {t('commerceWpaHomePromotions.clearLinkedProduct')}
                  </Button>
                ) : null}
              </Space>
            </Space>
          </Form.Item>
          <Form.Item name="productId" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('commerceWpaHomePromotions.pickProductTitle')}
        open={productPickerOpen}
        onCancel={() => setProductPickerOpen(false)}
        onOk={confirmPickProduct}
        width={1100}
        destroyOnClose
        okText={t('commerceWpaHomePromotions.pickerOk')}
        cancelText={t('commerceWpaHomePromotions.pickerCancel')}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto', paddingTop: 12 } }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="start">
            <Input.Search
              allowClear
              placeholder={t('commerceWpaHomePromotions.productSearchPlaceholder')}
              style={{ width: 280 }}
              onSearch={(v) => {
                setProductPickerSearch(v || '');
                setProductPickerPage(1);
              }}
              enterButton={<SearchOutlined />}
            />
            <Select
              allowClear
              placeholder={t('commerceProducts.status')}
              style={{ width: 140 }}
              options={PICKER_STATUS_OPTIONS}
              value={productPickerStatus}
              onChange={(v) => {
                setProductPickerStatus(v);
                setProductPickerPage(1);
              }}
            />
            <Select
              allowClear
              placeholder={t('commerceProducts.availability')}
              style={{ width: 160 }}
              options={PICKER_AVAILABILITY_OPTIONS}
              value={productPickerAvailability}
              onChange={(v) => {
                setProductPickerAvailability(v);
                setProductPickerPage(1);
              }}
            />
            <Input
              allowClear
              placeholder={t('commerceWpaHomePromotions.filterBrand')}
              style={{ width: 180 }}
              value={productPickerBrand}
              onChange={(e) => setProductPickerBrand(e.target.value)}
              onPressEnter={() => {
                productPickerBrandAppliedRef.current = productPickerBrand;
                if (productPickerPage === 1) loadProductPickerList();
                else setProductPickerPage(1);
              }}
            />
            <Button
              type="default"
              onClick={() => {
                productPickerBrandAppliedRef.current = productPickerBrand;
                if (productPickerPage === 1) loadProductPickerList();
                else setProductPickerPage(1);
              }}
            >
              {t('commerceWpaHomePromotions.refresh')}
            </Button>
          </Space>
          <Table
            rowKey="id"
            size="small"
            loading={productPickerLoading}
            columns={productPickerColumns}
            dataSource={productPickerRows}
            scroll={{ x: 1200 }}
            pagination={commerceServerTablePagination({
              page: productPickerPage,
              pageSize: productPickerPageSize,
              total: productPickerTotal,
              onChange: (p, ps) => {
                setProductPickerPage(p);
                if (ps) setProductPickerPageSize(ps);
              },
            })}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: pickerSelectedKey ? [pickerSelectedKey] : [],
              onChange: (keys, selRows) => {
                const id = keys[0] ?? null;
                setPickerSelectedKey(id);
                pickerSelectedRowRef.current = selRows?.[0] ?? null;
              },
            }}
            onRow={(row) => ({
              onClick: () => {
                setPickerSelectedKey(row.id);
                pickerSelectedRowRef.current = row;
              },
            })}
          />
        </Space>
      </Modal>
    </div>
  );
}
